import {
  Button,
  Checkbox,
  DatePickerDropdown,
  HSpacer,
  InfoTooltip,
  Input,
  Radio,
  Select,
  Text,
  VSpacer,
} from "@/components/DesignSystem";
import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { CircularProgress, Container, MenuItem, RadioGroup, Stack } from "@mui/material";
import { PromotionInputs } from "@/pages/Promotions/PromotionModal/PromotionModal";
import { useGetRetailerList, useManufacturerList } from "@/hooks/useProductQuery";
import Edit from "@mui/icons-material/Edit";
import { SponsorSelector } from "@/pages/Promotions/PromotionModal/SponsorSelector";
import { SponsorType } from "@shared/enums";
import { ApiPromotion, ApiRetailer, ApiRetailerLocation, ApiSubwallet } from "@api/interfaces";
import { EditLocationsModal } from "@/pages/Promotions/PromotionModal/EditLocationsModal";
import {
  getSelectedLocationsText,
  ProgramPeriodCheck,
  isEndDateInPast,
  isPointsExpirationBeforeEndDate,
  isPointsExpirationInPast,
  isPromotionDateRangeInverted,
} from "@/pages/Promotions/PromotionModal/Helpers";
import { ApiRewardsProgram } from "@api/interfaces/ApiRewardsProgram";
import {
  formatDateOnly,
  getJsDateFromString,
  PromotionProgramPeriodError,
} from "@shared/utilities";
import { SubwalletInputSelector } from "@/components/shared/Subwallet/SubwalletInputSelector";
import { isBoolean } from "lodash";

interface WorkflowStep1Props {
  activeRewardsProgram?: ApiRewardsProgram | null,
  canEditExpirationDate?: boolean,
  isAllLocationsLocked?: boolean,
  isEditMode?: boolean,
  locationIdsWithPointsAwarded?: string[],
  onPromotionChanged: (promotion: PromotionInputs) => void,
  programPeriodCheck?: ProgramPeriodCheck,
  promotion: PromotionInputs,
  retailerLocations?: ApiRetailerLocation[],
  selectedRetailer?: ApiRetailer,
  subwallets?: ApiSubwallet[],
}

export const WorkflowStep1 = ({
  activeRewardsProgram,
  canEditExpirationDate,
  isAllLocationsLocked,
  isEditMode,
  locationIdsWithPointsAwarded,
  onPromotionChanged,
  programPeriodCheck,
  promotion,
  retailerLocations,
  selectedRetailer,
  subwallets,
}: WorkflowStep1Props) => {
  const { t } = useTranslation();
  const [showEditLocationsModal, setShowEditLocationsModal] = React.useState(false);

  const { retailerList, isLoading: isRetailerListLoading } = useGetRetailerList({
    hasLocations: true,
  });

  const { manufacturerList } = useManufacturerList({
    ...(selectedRetailer && { retailerId: selectedRetailer.id }),
  }, {
    enabled: !!selectedRetailer && promotion.sponsorType === SponsorType.Manufacturer,
  });

  const promotionDateRangeError = useMemo(() => {
    if (isPromotionDateRangeInverted(promotion)) {
      return t($ => $.promotions.create.step1.startBeforeEnd);
    }
  }, [promotion, t]);

  const endDateError = useMemo(() => {
    if (isEditMode && isEndDateInPast(promotion)) {
      return t($ => $.promotions.create.step1.endDateNotInPast);
    }
  }, [isEditMode, promotion, t]);

  const programPeriodError = programPeriodCheck?.status === 'checked'
    ? programPeriodCheck.error
    : undefined;
  const startDateProgramError = (
    programPeriodError === PromotionProgramPeriodError.StartDateBeforeProgramStartDate
      ? t($ => $.promotions.create.step1.startBeforeProgramStart)
      : programPeriodError === PromotionProgramPeriodError.DatesNotWithinAProgramPeriod
        ? t($ => $.promotions.create.step1.notWithinProgramPeriod)
        : undefined
  );
  const endDateProgramError = (
    programPeriodError === PromotionProgramPeriodError.EndDateAfterProgramEndDate
      || programPeriodError === PromotionProgramPeriodError.EndDateAfterFutureProgramEndDate
      ? t($ => $.promotions.create.step1.endAfterProgramEnd)
      : programPeriodError === PromotionProgramPeriodError.DatesNotWithinAProgramPeriod
        ? ' '
        : undefined
  );

  // A date with its own error usually also inverts the range, so only that date is flagged.
  const hasSingleDateError = !!programPeriodError || !!endDateError;
  const dateRangeError = hasSingleDateError ? undefined : promotionDateRangeError;

  const pointsExpirationDateError = useMemo(() => {
    if (!promotion.endDate || !promotion.pointsExpirationDate) {
      return;
    }
    if (isPointsExpirationBeforeEndDate(promotion)) {
      return t($ => $.promotions.create.step1.expirationAfterEnd);
    }
    if (isPointsExpirationInPast(promotion)) {
      return t($ => $.promotions.create.step1.expirationFuture);
    }
  }, [promotion, t]);

  const startJsDate = promotion.startDate
    ? getJsDateFromString(promotion.startDate)
    : undefined;
  const endJsDate = promotion.endDate
    ? getJsDateFromString(promotion.endDate)
    : undefined;
  const pointsExpirationJsDate = promotion.pointsExpirationDate
    ? getJsDateFromString(promotion.pointsExpirationDate)
    : undefined;

  if (isRetailerListLoading) {
    return <Container>
      <Stack alignItems='center'>
        <VSpacer size='14' />
        <CircularProgress />
      </Stack>
    </Container>;
  }

  return <Stack sx={{ margin: '0 auto', maxWidth: '560px' }}>
    <Text category="title-medium">
      {t($ => $.promotions.create.step1.promotionName)}
    </Text>
    <VSpacer size="4"/>
    <Input
      label={t($ => $.promotions.create.step1.promotionNameLabel)}
      maxCharacterLimit={75}
      onChangeText={(name) => onPromotionChanged({
        ...promotion,
        name: name ? name : undefined,
      })}
      testID="create-promotion-modal-name-id"
      value={promotion.name}
    />
    <VSpacer size="8"/>
    <Text category="title-medium">
      {t($ => $.promotions.create.step1.retailer)}
    </Text>
    <VSpacer size="4"/>
    <Select
      disabled={isEditMode}
      label={t($ => $.promotions.create.step1.selectRetailer)}
      onChangeValue={(retailerID: string) => {
        onPromotionChanged({
          ...promotion,
          allLocations: undefined,
          locationIds: undefined,
          productIdsForEarningPoints: undefined,
          productIdsForRedeemingPoints: undefined,
          retailerId: retailerID,
        });
      }}
      testID="create-promotion-modal-retailer-id"
      value={selectedRetailer?.id}
    >
      {retailerList?.map((retailer) => (
        <MenuItem key={retailer.id} value={retailer.id}>
          {retailer.name}
        </MenuItem>
      ))}
    </Select>
    {!!retailerLocations?.length && (
      <>
        <VSpacer size="3" />
        <Stack alignItems="center" direction="row" spacing={2}>
          <Text category="body-large">
            {promotion.allLocations
              ? t($ => $.promotions.create.step1.allLocations)
              : getSelectedLocationsText(
                promotion.locationIds?.length ?? 0,
                retailerLocations?.length ?? 0,
              )
            }
          </Text>
          <Button
            color="primary"
            onClick={() => setShowEditLocationsModal(true)}
            startIcon={<Edit />}
            testID={`edit-retailer-locations-${selectedRetailer?.id}`}
            variant="text"
          >
            {t($ => $.common.actions.edit)}
          </Button>
        </Stack>
      </>
    )}
    {!!selectedRetailer && activeRewardsProgram !== undefined && (
      <>
        <VSpacer size="3" />
        <Text category="body-large" testID="create-promotion-modal-active-program-dates-id">
          {activeRewardsProgram
            ? t($ => $.promotions.create.step1.activeProgramDates, {
              endDate: activeRewardsProgram.endDate,
              startDate: activeRewardsProgram.startDate,
            })
            : t($ => $.promotions.create.step1.noActiveProgram)
          }
        </Text>
      </>
    )}
    <VSpacer size="8"/>
    <Text category="title-medium">
      {t($ => $.promotions.create.step1.runningDates)}
    </Text>
    <VSpacer size="4"/>
    <Stack direction="row" spacing={2}>
      <DatePickerDropdown
        disabled={isEditMode}
        errorMessage={startDateProgramError ?? dateRangeError}
        label={t($ => $.shared.dateRange.startDate)}
        onChange={(date) => onPromotionChanged({
          ...promotion,
          startDate: date ? formatDateOnly(date) : null,
        })}
        testID="create-promotion-modal-start-date-id"
        value={startJsDate}
      />
      <DatePickerDropdown
        errorMessage={
          endDateError
          ?? endDateProgramError
          ?? (dateRangeError && ' ')
        }
        label={t($ => $.shared.dateRange.endDate)}
        onChange={(date) => onPromotionChanged({
          ...promotion,
          endDate: date ? formatDateOnly(date) : null,
        })}
        testID="create-promotion-modal-end-date-id"
        value={endJsDate}
      />
    </Stack>
    <VSpacer size="8"/>
    <SponsorSelector
      disabled={isEditMode}
      onChange={(sponsorType: SponsorType) => {
        const updatedPromotion = {
          ...promotion,
          sponsorType,
          subwalletId: undefined,
        };
        if (sponsorType === SponsorType.Retailer) {
          updatedPromotion.manufacturerId = '';
        } else {
          updatedPromotion.manufacturerId = undefined;
        }
        onPromotionChanged(updatedPromotion);
      }}
      value={promotion.sponsorType}
    />
    {promotion.sponsorType === SponsorType.Manufacturer && (
      <>
        <VSpacer size="7" />
        <Select
          disabled={isEditMode || !selectedRetailer}
          label={t($ => $.promotions.create.step1.selectManufacturer)}
          onChangeValue={(manufacturerID: string) => {
            onPromotionChanged({
              ...promotion,
              manufacturerId: manufacturerID,
              productIdsForEarningPoints: undefined,
              productIdsForRedeemingPoints: undefined,
            });
          }}
          testID="create-promotion-modal-manufacturer-id"
          value={promotion.manufacturerId}
        >
          {manufacturerList?.map((manufacturer) => (
            <MenuItem key={manufacturer.id} value={manufacturer.id}>
              {manufacturer.name}
            </MenuItem>
          ))}
        </Select>
      </>
    )}
    <VSpacer size="8"/>
    {!!subwallets && (
      <SubwalletInputSelector
        disabled={isEditMode}
        onChange={(subwalletId) => {
          onPromotionChanged({
            ...promotion,
            subwalletId,
          });
        }}
        promotion={promotion as ApiPromotion}
        selectedSubwalletId={promotion.subwalletId}
      />
    )}
    <Text category="title-medium">
      {t($ => $.promotions.create.step1.pointsExpiration)}
    </Text>
    <VSpacer size="4"/>
    <DatePickerDropdown
      disabled={!canEditExpirationDate}
      errorMessage={pointsExpirationDateError}
      label={t($ => $.promotions.create.step1.pointsExpiration)}
      onChange={(date) => onPromotionChanged({
        ...promotion,
        pointsExpirationDate: date ? formatDateOnly(date) : null,
      })}
      testID="create-promotion-modal-points-expiration-date-id"
      value={pointsExpirationJsDate}
    />
    <VSpacer size="8"/>
    <Text category="title-medium">
      {t($ => $.promotions.create.step1.redeemingPoints)}
    </Text>
    <VSpacer size="4"/>
    <RadioGroup
      name="redeem-points-radio-group"
      onChange={(e) => {
        onPromotionChanged({
          ...promotion,
          redeemOnAllProducts: e.target.value === "all-products",
        });
      }}
      row
      value={isBoolean(promotion.redeemOnAllProducts)
        ? promotion.redeemOnAllProducts ? "all-products" : "select-products"
        : null
      }>
      <Radio
        disabled={isEditMode}
        testID="redeem-points-radio-option-all-products"
        value={"all-products"}
      >
        <Text category="title-medium">
          {promotion.sponsorType === SponsorType.Retailer
            ? t($ => $.promotions.create.step1.anyProduct)
            : t($ => $.promotions.create.step1.anyManufacturerProduct)
          }
        </Text>
      </Radio>
      <VSpacer size="4"/>
      <Radio
        disabled={isEditMode}
        testID="redeem-points-radio-option-select-products"
        value={"select-products"}
      >
        <Text category="title-medium">
          {promotion.sponsorType === SponsorType.Retailer
            ? t($ => $.promotions.create.step1.selectProducts)
            : t($ => $.promotions.create.step1.selectManufacturerProducts)
          }
        </Text>
      </Radio>
    </RadioGroup>
    <VSpacer size="8"/>
    <Stack alignItems="center" direction="row">
      <Text category="title-medium">
        {t($ => $.promotions.create.step1.dashboardDisplay)}
      </Text>
      <HSpacer size="3"/>
      <InfoTooltip>
        {t($ => $.promotions.create.step1.dashboardDisplayTooltip)}
      </InfoTooltip>
    </Stack>
    <VSpacer size="4"/>
    <Checkbox
      alignItems="flex-start"
      checked={promotion.showInDashboardCarousel}
      onChangeChecked={(showInDashboardCarousel) => onPromotionChanged({
        ...promotion,
        showInDashboardCarousel,
      })}
      testID="display-promotion-on-carousel-checkbox-id"
    >
      <Text category="title-medium">
        {t($ => $.promotions.create.step1.includeCarousel)}
      </Text>
    </Checkbox>
    <VSpacer size="4"/>
    <Checkbox
      alignItems="flex-start"
      checked={promotion.showInDashboardTable}
      onChangeChecked={(showInDashboardTable) => onPromotionChanged({
        ...promotion,
        showInDashboardTable,
      })}
      testID="display-promotion-in-dashboard-table-checkbox-id"
    >
      <Text category="title-medium">
        {t($ => $.promotions.create.step1.includeTierDetails)}
      </Text>
    </Checkbox>
    <VSpacer size="8"/>
    {!!activeRewardsProgram?.grantBothProgramAndPromotionPoints && (
      <>
        <Text category="title-medium">
          {t($ => $.promotions.create.step1.engineOverride)}
        </Text>
        <VSpacer size="4"/>
        <Checkbox
          alignItems="flex-start"
          checked={promotion.overridePointsEngine}
          disabled={isEditMode}
          onChangeChecked={(overridePointsEngine) => onPromotionChanged({
            ...promotion,
            overridePointsEngine,
          })}
          testID="override-points-engine-checkbox-id"
        >
          <Text category="title-medium">
            {t($ => $.promotions.create.step1.manufacturerOnly)}
          </Text>
        </Checkbox>
        <VSpacer size="8"/>
      </>
    )}
    <Text category="title-medium">
      {t($ => $.promotions.create.step1.manualAdministration)}
    </Text>
    <VSpacer size="4"/>
    <Checkbox
      alignItems="flex-start"
      checked={promotion.grantsPointsManually}
      disabled={isEditMode}
      onChangeChecked={(grantsPointsManually) => onPromotionChanged({
        ...promotion,
        grantsPointsManually,
      })}
      testID="manually-administered-checkbox-id"
    >
      <Text category="title-medium">
        {t($ => $.promotions.create.step1.doNotAutoGrantPoints)}
      </Text>
    </Checkbox>
    {!!selectedRetailer && !!retailerLocations?.length && showEditLocationsModal && (
      <EditLocationsModal
        isAllLocationsLocked={isAllLocationsLocked}
        locationIdsWithPointsAwarded={locationIdsWithPointsAwarded}
        onClose={() => setShowEditLocationsModal(false)}
        onPromotionChanged={onPromotionChanged}
        promotion={promotion}
        retailerLocations={retailerLocations}
        selectedRetailer={selectedRetailer}
      />
    )}
  </Stack>;
};
